import { create } from "zustand";
import { cartApi } from "../lib/api/cart";
import { couponsApi } from "../lib/api/coupons";
import toast from "react-hot-toast";
import { getProductImages } from "../lib/imageUtils";
import {
	loadGuestCart,
	saveGuestCart,
	clearGuestCart,
	addToGuestCart,
	removeFromGuestCart,
	updateGuestCartQuantity,
	calculateGuestCartTotals,
	formatGuestCartForServer,
	hasGuestCart
} from "../lib/guestCart";
import { useUserStore } from "./useUserStore";

// פונקציה להמרת נתוני עגלה
const transformCartData = (cartData) => {
	if (!cartData || !Array.isArray(cartData)) return [];
	
    return cartData.map(item => {
        const product = item.product || {};
        const productImage = getProductImages(product)[0];
        const flat = {
            ...item,
            // שדות שטוחים לשימוש נוח ברכיבים קיימים
            name: item.name || product.name,
            image: item.image || productImage,
            price: typeof item.price === 'number' ? item.price : product.price,
            product: {
                ...product,
                image: product.image || productImage || '/images/products/placeholder.svg'
            }
        };
        return flat;
    });
};

export const useCartStore = create((set, get) => ({
	cart: [],
	coupon: null,
	total: 0,
	subtotal: 0,
	shippingMethod: 'standard', // 'standard' | 'free' | 'pickup'
	shippingCost: 46,
	isCouponApplied: false,
	isLoggedIn: false,
	isGuestMode: false,
	
	// עדכון שיטת המשלוח
	setShippingMethod: (method) => {
		const valid = ['standard', 'free', 'pickup'];
		const chosen = valid.includes(method) ? method : 'standard';
		set({ shippingMethod: chosen });
		get().calculateTotals();
	},

	getMyCoupon: async () => {
		try {
			const coupon = await couponsApi.getMyCoupon();
			set({ coupon });
		} catch (error) {
			if (error?.response?.status === 401) {
				// אם המשתמש לא מחובר, לא נציג שגיאה - פשוט לא נטען קופונים
				console.log("User not authenticated for coupons");
				set({ coupon: null });
				return;
			}
			if (error?.response?.status === 500) {
				// אם שגיאת שרת, נאפס את הקופון ולא נציג שגיאה למשתמש
				console.log("Server error fetching coupons, resetting coupon state");
				set({ coupon: null });
				return;
			}
			console.error("Error fetching coupon:", error);
		}
	},
	applyCoupon: async (code) => {
		try {
			const coupon = await couponsApi.validateCoupon(code);
			set({ coupon, isCouponApplied: true });
			get().calculateTotals();
			toast.success("קופון הוחל בהצלחה");
		} catch (error) {
			if (error?.response?.status === 401) {
				toast.error("יש להתחבר כדי להשתמש בקופונים");
				window.location.href = "/login";
				return;
			}
			const message = error?.response?.data?.message || error?.message || "נכשל ביישום הקופון";
			toast.error(message);
		}
	},
	removeCoupon: () => {
		set({ coupon: null, isCouponApplied: false });
		get().calculateTotals();
		toast.success("Coupon removed");
	},

	getCartItems: async () => {
		try {
			const cartItems = await cartApi.getCartItems();
			const transformedItems = transformCartData(cartItems);
			set({ cart: transformedItems });
			get().calculateTotals();
		} catch (error) {
			set({ cart: [] });
			// Don't show error toast for 401 (unauthorized) - user might not be logged in
			if (error?.response?.status !== 401) {
				const message = error?.response?.data?.message || error?.message || "An error occurred";
				toast.error(message);
			}
		}
	},
	
	// פונקציה לרענון העגלה עם נתוני תמונות מעודכנים
	refreshCart: async () => {
		await get().getCartItems();
		toast.success("העגלה עודכנה");
	},
	clearCart: async () => {
		const { isGuestMode } = get();
		
		if (isGuestMode) {
			clearGuestCart();
		}
		
		set({ 
			cart: [], 
			coupon: null, 
			total: 0, 
			subtotal: 0,
			isGuestMode: false
		});
	},
	addToCart: async (product) => {
		const { isLoggedIn } = get();
		
		if (!isLoggedIn) {
			// אם המשתמש לא מחובר, שמור בסל אורחים
			try {
				const updatedGuestCart = addToGuestCart(product, 1);
				const transformedCart = updatedGuestCart.map(item => ({
					...item,
					product: {
						...item.product,
						image: getProductImages(item.product)[0] || '/images/products/placeholder.jpg'
					}
				}));
				
				set({ 
					cart: transformedCart,
					isGuestMode: true
				});
				get().calculateTotals();
				toast.success("המוצר נוסף לעגלה (נשמר באופן זמני)");
			} catch (error) {
				console.error('Error adding to guest cart:', error);
				toast.error("אירעה שגיאה בהוספת המוצר לעגלה");
			}
			return;
		}
		
		// אם המשתמש מחובר, שמור בשרת
		try {
			const cartItems = await cartApi.addToCart(product._id, 1);
			toast.success("המוצר נוסף לעגלה בהצלחה");

			const transformedItems = transformCartData(cartItems);
			set({ cart: transformedItems });
			get().calculateTotals();
		} catch (error) {
			// אם שגיאת 401 (לא מורשה), נעבור למצב אורח
			if (error?.response?.status === 401) {
				set({ isLoggedIn: false });
				// נסה שוב במצב אורח
				get().addToCart(product);
				return;
			}
			const message = error?.response?.data?.message || error?.message || "אירעה שגיאה בהוספת המוצר לעגלה";
			toast.error(message);
		}
	},
	removeFromCart: async (productId) => {
		const { isLoggedIn, isGuestMode } = get();
		
		if (!isLoggedIn || isGuestMode) {
			// מצב אורח
			try {
				const updatedGuestCart = removeFromGuestCart(productId);
				const transformedCart = updatedGuestCart.map(item => ({
					...item,
					product: {
						...item.product,
						image: getProductImages(item.product)[0] || '/images/products/placeholder.jpg'
					}
				}));
				
				set({ cart: transformedCart });
				get().calculateTotals();
				toast.success("המוצר הוסר מהעגלה");
			} catch (error) {
				console.error('Error removing from guest cart:', error);
				toast.error("אירעה שגיאה בהסרת המוצר");
			}
			return;
		}
		
		// מצב מחובר
		try {
			const cartItems = await cartApi.removeFromCart(productId);
			const transformedItems = transformCartData(cartItems);
			set({ cart: transformedItems });
			get().calculateTotals();
		} catch (error) {
			if (error?.response?.status === 401) {
				set({ isLoggedIn: false });
				toast.error("יש להתחבר כדי לנהל את העגלה");
				return;
			}
			const message = error?.response?.data?.message || error?.message || "אירעה שגיאה בהסרת המוצר מהעגלה";
			toast.error(message);
		}
	},
	updateQuantity: async (productId, quantity) => {
		if (quantity === 0) {
			get().removeFromCart(productId);
			return;
		}
		
		const { isLoggedIn, isGuestMode } = get();
		
		if (!isLoggedIn || isGuestMode) {
			// מצב אורח
			try {
				const updatedGuestCart = updateGuestCartQuantity(productId, quantity);
				const transformedCart = updatedGuestCart.map(item => ({
					...item,
					product: {
						...item.product,
						image: getProductImages(item.product)[0] || '/images/products/placeholder.jpg'
					}
				}));
				
				set({ cart: transformedCart });
				get().calculateTotals();
			} catch (error) {
				console.error('Error updating guest cart quantity:', error);
				toast.error("אירעה שגיאה בעדכון הכמות");
			}
			return;
		}

		// מצב מחובר
		try {
			const cartItems = await cartApi.updateQuantity(productId, quantity);
			const transformedItems = transformCartData(cartItems);
			set({ cart: transformedItems });
			get().calculateTotals();
		} catch (error) {
			if (error?.response?.status === 401) {
				set({ isLoggedIn: false });
				toast.error("יש להתחבר כדי לנהל את העגלה");
				return;
			}
			const message = error?.response?.data?.message || error?.message || "אירעה שגיאה בעדכון כמות המוצר";
			toast.error(message);
		}
	},
	calculateTotals: () => {
		const { cart, coupon, isGuestMode, shippingMethod } = get();
		
		if (isGuestMode) {
			// חישוב עבור סל אורחים
			const totals = calculateGuestCartTotals(cart, coupon);
			
			// משלוח עבור אורח: חינם מעל 299, איסוף עצמי 0, רגיל 29
			const guestSubtotal = totals.subtotal || 0;
			let shippingCost = 0;
			if (shippingMethod === 'pickup') shippingCost = 0;
			else if (guestSubtotal >= 299 || shippingMethod === 'free') shippingCost = 0;
			else shippingCost = 46;

			set({ 
				subtotal: totals.subtotal, 
				shippingCost,
				total: Number((totals.total + shippingCost).toFixed(2))
			});
			return;
		}
		
        // חישוב רגיל עבור משתמשים מחוברים
        const subtotal = cart.reduce((sum, item) => {
            const price = Number(item.price ?? item.product?.price ?? 0);
            const quantity = Number(item.quantity) || 0;
            return sum + (price * quantity);
        }, 0);
		
		let total = subtotal;

		if (coupon && coupon.discountPercentage) {
			const discount = subtotal * (Number(coupon.discountPercentage) / 100);
			total = Math.max(0, subtotal - discount);
		}

		// חישוב משלוח: חינם מעל 299, איסוף עצמי 0, רגיל 29
		let shippingCost = 0;
		if (shippingMethod === 'pickup') shippingCost = 0;
		else if (subtotal >= 299 || shippingMethod === 'free') shippingCost = 0;
		else shippingCost = 46;

		set({ 
			subtotal: Number(subtotal.toFixed(2)), 
			shippingCost,
			total: Number((total + shippingCost).toFixed(2)) 
		});
	},
	
	// פונקציות לניהול מצב התחברות ומיזוג סל
	setUserLoginStatus: (isLoggedIn) => {
		set({ isLoggedIn });
		
		if (isLoggedIn) {
			// כשהמשתמש מתחבר, בדוק אם יש סל אורחים למיזוג
			get().mergeGuestCartOnLogin();
		} else {
			// כשהמשתמש מתנתק, טען סל אורחים
			get().loadGuestCartData();
		}
	},
	
	loadGuestCartData: () => {
		try {
			const guestCart = loadGuestCart();
			if (guestCart.length > 0) {
				const transformedCart = guestCart.map(item => ({
					...item,
					product: {
						...item.product,
						image: getProductImages(item.product)[0] || '/images/products/placeholder.jpg'
					}
				}));
				
				set({ 
					cart: transformedCart,
					isGuestMode: true
				});
				get().calculateTotals();
				console.log('📦 Guest cart loaded:', guestCart.length, 'items');
			}
		} catch (error) {
			console.error('Error loading guest cart:', error);
		}
	},
	
	mergeGuestCartOnLogin: async () => {
		try {
			if (!hasGuestCart()) {
				console.log('📦 No guest cart to merge');
				// אם אין סל אורחים, פשוט טען את סל המשתמש
				await get().getCartItems();
				return;
			}
			
			const guestCart = loadGuestCart();
			console.log('🔀 Merging guest cart with', guestCart.length, 'items');
			
			// הוסף כל מוצר מסל האורחים לסל המשתמש
			for (const item of guestCart) {
				try {
					await cartApi.addToCart(item.product._id, item.quantity);
				} catch (error) {
					console.error('Error merging item:', item.product.name, error);
				}
			}
			
			// נקה את סל האורחים
			clearGuestCart();
			
			// טען את הסל המעודכן מהשרת
			await get().getCartItems();
			
			set({ isGuestMode: false });
			toast.success('מסל האורחים שלך מוזג בהצלחה!');
			
		} catch (error) {
			console.error('Error merging guest cart:', error);
			toast.error('אירעה שגיאה במיזוג הסל');
		}
	},
	
	clearGuestCartData: () => {
		clearGuestCart();
		set({ 
			cart: [],
			isGuestMode: false
		});
		get().calculateTotals();
	},
	
	// פונקציה לאתחול הסל בעת טעינת האפליקציה
	initializeCart: () => {
		const userStore = useUserStore.getState();
		const isLoggedIn = !!userStore.user;
		
		set({ isLoggedIn });
		
		if (isLoggedIn) {
			// אם המשתמש מחובר, טען את הסל מהשרת
			get().getCartItems();
			get().getMyCoupon();
		} else {
			// אם המשתמש לא מחובר, טען סל אורחים
			get().loadGuestCartData();
		}
	},
}));
